import { useQuery } from '@apollo/client';
import { QueryString, OptionalString, OptionalNumber } from '@sixfold/app-utils';
import { useParams, useLocation, useNavigate } from 'react-router';

import { VehicleMutation } from './vehicle_mutation_container';
import { isValid, endOf, substract } from '../../lib/date';
import {
  VehicleQuery,
  VehicleQueryVariables,
  VehicleHistoryQuery as VehicleHistoryQueryResult,
  VehicleHistoryQueryVariables,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import {
  HISTORY_SIMPLIFICATION_QS,
  TIMELINE_TIMESTAMP_QS,
  TIMELINE_START_TIME_QS,
  TIMELINE_END_TIME_QS,
} from '../../map/components/map_slider';
import { VehicleViewDataProps, VehicleView } from '../components/vehicle';
import { vehicleQuery, vehicleHistoryQuery } from '../graphql';
import { processVehicleHistoryData } from '../utils';

class LoadingContainer extends Loader<VehicleQuery, VehicleViewDataProps, VehicleQueryVariables> {}

export const VehicleContainer = () => {
  const location = useLocation();
  return (
    <QueryString
      queryString={location.search}
      queryParametersToParse={{
        [TIMELINE_TIMESTAMP_QS]: OptionalString,
        [HISTORY_SIMPLIFICATION_QS]: OptionalNumber,
        [TIMELINE_START_TIME_QS]: OptionalString,
        [TIMELINE_END_TIME_QS]: OptionalString,
      }}>
      {({ parsedQueryString: { timestamp, history_simplification_tolerance, timeline_start, timeline_end } }) => {
        const selectedTimestamp = isValid(new Date(timestamp ?? '')) ? timestamp : undefined;

        const endTime = timeline_end !== undefined ? timeline_end : endOf(new Date(), 'hour').toISOString();
        const startTime =
          timeline_start !== undefined ? timeline_start : substract(endTime, { days: 10 }).toISOString();

        return (
          <VehicleContainerInner
            selectedTimestamp={selectedTimestamp}
            startTime={startTime}
            endTime={endTime}
            history_simplification_tolerance={history_simplification_tolerance}
          />
        );
      }}
    </QueryString>
  );
};

const VehicleContainerInner = ({
  selectedTimestamp,
  startTime,
  endTime,
  history_simplification_tolerance,
}: {
  selectedTimestamp: string | undefined;
  startTime: string;
  endTime: string;
  history_simplification_tolerance: number | undefined;
}) => {
  const { vehicle_id } = useParams() as { vehicle_id: string };
  const navigate = useNavigate();

  const historyResult = useQuery<VehicleHistoryQueryResult, VehicleHistoryQueryVariables>(vehicleHistoryQuery, {
    variables: {
      vehicle_id,
      history_start_time: startTime,
      history_end_time: endTime,
      simplification_tolerance: history_simplification_tolerance,
    },
  });

  const vehicleResult = useQuery<VehicleQuery, VehicleQueryVariables>(vehicleQuery, {
    variables: {
      vehicle_id,
    },
  });

  const { vehicleHistory: history, breakHistory } = processVehicleHistoryData(historyResult.data, new Date(endTime));

  return (
    <VehicleMutation>
      {({ deleteVehicle }) => (
        <LoadingContainer
          result={vehicleResult}
          mapData={({ data }) => ({ data: { vehicle: data.vehicle, history, breakHistory } })}>
          {({ data }) => (
            <VehicleView
              deleteVehicle={async (vehicleId) => {
                await deleteVehicle(vehicleId, data.vehicle?.company.company_id ?? '');
                void navigate(-1);
              }}
              timeline={{
                selectedTimestamp,
                startTime,
                endTime,
              }}
              data={data}
            />
          )}
        </LoadingContainer>
      )}
    </VehicleMutation>
  );
};
